"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { AppContext } from "@/App"
import { Button } from "@/Components/ui/button"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/Components/ui/form"
import { Textarea } from "@/Components/ui/textarea"

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/Components/ui/select"
import DatePicker from "@/Components/ui/DatePicker"

import { ProjectChangeRequest } from "@/Components/Charter/projectChange_table/columns";
import React from "react"
import { useIsMobile } from "@/hooks/use-mobile";

const formSchema = z.object({
    description: z
        .string()
        .min(10, {
            message: "Request must be at least 10 characters.",
        })
        .max(250, {
            message: "Request must not be longer than 250 characters.",
        }),
    owner: z.string(),
    date: z.date(),
    raisedBy: z.string().optional(),
    decisionMaker: z.string().optional(),
    dueDate: z.date(),
    closedDate: z.date().optional(),
    reviewStatus: z.enum(["Pending", "Approved", "Rejected"]),
    status: z.enum(["Cancelled", "Complete", "Open", "Closed"]),
    workstream: z
        .string({
            required_error: "Please select a Workstream",
        }),
    workPackage: z.string({
        required_error: "Please select a work package",
    }),
    rating: z.enum(["Low", "Medium", "High"]),
    notes: z.string().max(500),
})

interface ProjectChangeRequestFormProps {
    projectChangeRequest?: ProjectChangeRequest,
    saveProjectChangeRequest: (projectChangeRequest: ProjectChangeRequest) => void,
}

function ProjectChangeRequestForm({ projectChangeRequest, saveProjectChangeRequest }: ProjectChangeRequestFormProps) {
    const app = React.useContext(AppContext);
    const teamOptions = app.projectManagers?.concat(app.teamMembers).concat(app.sponsors) || [];
    const isMobile = useIsMobile();

    const [descriptionLength, setDescriptionLength] = React.useState(projectChangeRequest ? projectChangeRequest.description.length : 0);
    const [notesLength, setNotesLength] = React.useState(projectChangeRequest ? projectChangeRequest.notes.length : 0);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            workstream: projectChangeRequest ? projectChangeRequest.workstream : (app.selectedProject?.projectTypeId === 1 ? app.workPackageOptions?.[0]?.workstreamName : ""),
            workPackage: projectChangeRequest ? projectChangeRequest.workPackage : "",
            rating: projectChangeRequest ? (projectChangeRequest.rating as "Low" | "Medium" | "High") : "Low",
            description: projectChangeRequest ? projectChangeRequest.description : "",
            owner: projectChangeRequest ? projectChangeRequest.userId === "" ? "unassigned" : projectChangeRequest.userId : "unassigned",
            date: projectChangeRequest ? new Date(projectChangeRequest.date) : new Date(),
            raisedBy: projectChangeRequest ? projectChangeRequest.raisedBy : app.applicationUser?.name,
            decisionMaker: projectChangeRequest ? projectChangeRequest.decisionMaker : app.applicationUser?.name,
            dueDate: projectChangeRequest ? projectChangeRequest.dueDate ? new Date(projectChangeRequest.dueDate) : new Date() : new Date(),
            closedDate: projectChangeRequest ? projectChangeRequest.closedDate ? new Date(projectChangeRequest.closedDate) : undefined : undefined,
            reviewStatus: projectChangeRequest ? projectChangeRequest.reviewStatus as "Pending" | "Approved" | "Rejected" : "Pending",
            status: projectChangeRequest ? projectChangeRequest.status as "Cancelled" | "Complete" | "Open" | "Closed" : "Open",
            notes: projectChangeRequest ? projectChangeRequest.notes : "",
        },
    })

    // 2. Define a submit handler.
    function onSubmit(data: z.infer<typeof formSchema>) {
        console.log("Form data", data)

        const newProjectChangeRequest: ProjectChangeRequest = {
            projectId: app.selectedProject?.id || 0,
            projectChangeRequestId: projectChangeRequest ? projectChangeRequest.projectChangeRequestId : 0,
            workstream: app.workPackageOptions?.[0]?.workstreamName || "",
            workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0,
            workPackageId: app.workPackageOptions?.find((ws) => ws.workstreamName === data.workstream)?.workPackageNames
                .find((wp) => wp.workPackageName === data.workPackage)?.workPackageId || 0,
            workPackage: data.workPackage,
            rating: data.rating,
            description: data.description,
            owner: teamOptions.find(t => t.id == data.owner)?.displayName ?? "Unassigned",
            userId: data.owner == "unassigned" ? "" : data.owner,
            date: new Date(data.date),
            raisedBy: data.raisedBy,
            decisionMaker: data.decisionMaker,
            dueDate: new Date(data.dueDate),
            closedDate: data.closedDate ? new Date(data.closedDate) : undefined,
            reviewStatus: "Pending" as "Pending" | "Approved" | "Rejected",
            status: projectChangeRequest ? projectChangeRequest.status as "Cancelled" | "Complete" | "Open" | "Closed" : "Open",
            notes: data.notes,
            seen: false,
            acknowledged: false,
        }

        saveProjectChangeRequest(newProjectChangeRequest);

        if (projectChangeRequest == null || projectChangeRequest == undefined) {
            form.setValue("description", "");
            form.setValue("notes", "");

            setTimeout(() => form.setFocus("description"), 0);
        }
    }

    if (isMobile) {
        return (
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-4">
                    <FormField
                        control={form.control}
                        name="workPackage"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-2xl">Work Package</FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <FormControl>
                                        <SelectTrigger className="text-2xl w-full p-2">
                                            <SelectValue placeholder="Select a work package" />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {app.workPackageOptions?.find((ws) => ws.workstreamName === form.watch("workstream"))?.workPackageNames?.map((wp) => (
                                            <SelectItem key={wp.workPackageId} value={wp.workPackageName}>{wp.workPackageName}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="flex flex-row justify-between">
                        <FormField
                            control={form.control}
                            name="rating"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Rating</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="text-2xl w-[200px] p-2">
                                                <SelectValue placeholder="Select a Priority Level" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="High">High</SelectItem>
                                            <SelectItem value="Medium">Medium</SelectItem>
                                            <SelectItem value="Low">Low</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="status"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Status</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value} disabled={true}>
                                        <FormControl>
                                            <SelectTrigger className="text-2xl w-[200px] p-2">
                                                <SelectValue placeholder="Select a Status" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Open">Open</SelectItem>
                                            <SelectItem value="Closed">Closed</SelectItem>
                                            <SelectItem value="Cancelled">Cancelled</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="closedDate"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Closed Date</FormLabel>
                                    <FormControl>
                                        {field.value ? (
                                            <DatePicker className="text-2xl w-[200px]" date={new Date(field.value)} setDate={field.onChange} />
                                        ) : (
                                            <div className="w-[200px] h-9 border rounded-md bg-muted text-muted-foreground text-2xl"><div className="mx-auto my-auto w-min">-</div></div>
                                        )}
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    <div className="flex flex-row justify-between">
                        <FormField
                            control={form.control}
                            name="owner"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Owner</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="text-2xl w-[425px] p-2">
                                                    <SelectValue placeholder="Owner..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.id as string}>{t.displayName}</SelectItem>
                                                ))}
                                                <SelectItem key={"unassigned"} value={"unassigned"}>Unassigned</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="dueDate"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Due Date</FormLabel>
                                    <FormControl>
                                        <DatePicker className="text-2xl w-[200px]" date={new Date(field.value)} setDate={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="flex flex-row justify-between">
                        <FormField
                            control={form.control}
                            name="raisedBy"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Raised By</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="text-2xl w-[425px] p-2">
                                                    <SelectValue placeholder="Raised By..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.displayName}>{t.displayName}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="date"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Date Raised</FormLabel>
                                    <FormControl>
                                        <DatePicker className="text-2xl w-[200px]" date={new Date(field.value)} setDate={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="flex flex-row justify-between">
                        <FormField
                            control={form.control}
                            name="decisionMaker"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Decision Maker</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="text-2xl w-[425px] p-2">
                                                    <SelectValue placeholder="Decision Maker..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.displayName}>{t.displayName}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="reviewStatus"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Outcome</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value} disabled={true}>
                                        <FormControl>
                                            <SelectTrigger className="text-2xl w-[200px] p-2">
                                                <SelectValue placeholder="Select an Outcome" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Pending">Pending</SelectItem>
                                            <SelectItem value="Approved">Approved</SelectItem>
                                            <SelectItem value="Rejected">Rejected</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between text-2xl"><div>Request</div><div><span className="text-xs flex text-gray-500 items-end pr-2">{descriptionLength}/250</span></div></FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter the details of your project change request..."
                                        className="resize-none text-2xl w-full"
                                        {...field}
                                        rows={3}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setDescriptionLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between text-2xl"><div>Notes </div><div><span className="text-xs flex text-gray-500 items-end pr-2">{notesLength}/500</span></div></FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter any notes here..."
                                        className="resize-none text-2xl w-full"
                                        rows={6}
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setNotesLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div><Button className="text-2xl w-full ml-auto" type="submit">Submit</Button></div>
                </form>
            </Form >
        );
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <div className="flex flex-col space-y-4">
                    <div className="flex flex-row space-x-4">
                        <FormField
                            control={form.control}
                            name="workPackage"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Work Package</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="w-48 p-2">
                                                <SelectValue placeholder="Select a work package" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {app.workPackageOptions?.find((ws) => ws.workstreamName === form.watch("workstream"))?.workPackageNames?.map((wp) => (
                                                <SelectItem key={wp.workPackageId} value={wp.workPackageName}>{wp.workPackageName}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="rating"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Rating</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="w-48 p-2">
                                                <SelectValue placeholder="Select a Priority Level" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="High">High</SelectItem>
                                            <SelectItem value="Medium">Medium</SelectItem>
                                            <SelectItem value="Low">Low</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="status"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Status</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value} disabled={true}>
                                        <FormControl>
                                            <SelectTrigger className="w-48 p-2">
                                                <SelectValue placeholder="Select a Status" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Open">Open</SelectItem>
                                            <SelectItem value="Closed">Closed</SelectItem>
                                            <SelectItem value="Cancelled">Cancelled</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="reviewStatus"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Outcome</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value} disabled={true}>
                                        <FormControl>
                                            <SelectTrigger className="w-48 p-2">
                                                <SelectValue placeholder="Select an Outcome" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Pending">Pending</SelectItem>
                                            <SelectItem value="Approved">Approved</SelectItem>
                                            <SelectItem value="Rejected">Rejected</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="date"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Date Raised</FormLabel>
                                    <FormControl>
                                        <DatePicker className="w-48" date={new Date(field.value)} setDate={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="flex flex-row space-x-4">
                        <FormField
                            control={form.control}
                            name="closedDate"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Closed Date</FormLabel>
                                    <FormControl>
                                        {field.value ? (
                                            <DatePicker className="w-48" date={new Date(field.value)} setDate={field.onChange} />
                                        ) : (
                                            <div className="w-48 h-9 border rounded-md bg-muted text-muted-foreground"><div className="mx-auto my-auto w-min">-</div></div>
                                        )}
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="dueDate"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Due Date</FormLabel>
                                    <FormControl>
                                        <DatePicker className="w-48" date={new Date(field.value)} setDate={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="owner"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Owner</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="w-48 p-2">
                                                    <SelectValue placeholder="Owner..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.id as string}>{t.displayName}</SelectItem>
                                                ))}
                                                <SelectItem key={"unassigned"} value={"unassigned"}>Unassigned</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="raisedBy"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Raised By</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="w-48 p-2">
                                                    <SelectValue placeholder="Raised By..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.displayName}>{t.displayName}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="decisionMaker"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Decision Maker</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="w-48 p-2">
                                                    <SelectValue placeholder="Decision Maker..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.displayName}>{t.displayName}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between"><div>Request</div><div><span className="text-xs flex text-gray-500 items-end pr-2">{descriptionLength}/250</span></div></FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter the details of your projectChange request..."
                                        className="resize-none"
                                        {...field}
                                        rows={3}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setDescriptionLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between"><div>Notes </div><div><span className="text-xs flex text-gray-500 items-end pr-2">{notesLength}/500</span></div></FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter any notes here..."
                                        className="resize-none"
                                        rows={6}
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setNotesLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div><Button className="w-48 ml-auto" type="submit">Submit</Button></div>
                </div>
            </form>
        </Form >
    )
}

export default ProjectChangeRequestForm;
